import { MessageOutlined } from "@ant-design/icons";
import { Alert, Avatar, Rate, Space, Switch, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Table } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { AdminComment, CommentStatus } from "@/entities/comment";
import { useComments } from "@/entities/comment";
import { useAuth } from "@/entities/session";
import { CreatePersonaCommentButton } from "@/features/create-persona-comment";
import { CommentActions } from "@/features/moderate-comment";
import { errorMessage } from "@/shared/api";
import { SECTIONS } from "@/shared/config";
import { formatDateTime } from "@/shared/lib";
import {
  EmptyState,
  LoadMore,
  PageHeader,
  SectionCard,
  StatusTag,
  UserCell,
} from "@/shared/ui";
import { StatusFilter } from "@/widgets/request-table";

/**
 * Очередь модерации отзывов (доработки §6.2, §7 «Comments»).
 *
 * **Фильтр по умолчанию пуст, а не «все».** Пустой фильтр бэкенд понимает как
 * очередь: отзывы с жалобами и задержанные фильтром спама. Отзывы публикуются
 * сразу, поэтому «все» — это весь поток, среди которого две строки, требующие
 * решения, пришлось бы искать глазами.
 *
 * **Текст отзыва рендерится через `Typography.Paragraph`, а не как HTML.**
 * Бэкенд хранит его дословно, вместе с `<script>`: экранирование — забота
 * потребителя, и здесь его обеспечивает React.
 */
const STATUSES: readonly CommentStatus[] = [
  "flagged",
  "pending",
  "published",
  "hidden",
] as const;

export function CommentsPage() {
  const { t } = useTranslation();
  const { isSuperadmin } = useAuth();
  const [statuses, setStatuses] = useState<CommentStatus[]>([]);
  const [onlyReported, setOnlyReported] = useState(false);

  const query = useComments({ statuses, hasReports: onlyReported ? true : null });
  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<AdminComment> = [
    { title: t("common.id"), dataIndex: "id", width: 76 },
    {
      title: t("common.user"),
      key: "user",
      width: 180,
      render: (_, row) => {
        if (row.author) {
          return (
            <UserCell
              id={row.author.id}
              username={row.author.username}
              firstName={row.author.display_name}
              telegramId={row.author.telegram_id}
            />
          );
        }
        // Синтетический отзыв: аккаунта за ним нет, но имя есть. Проверяется
        // раньше поддержки, потому что `author === null` теперь означает уже не
        // одно, а два разных состояния — и без этой ветки персона подписалась
        // бы «Поддержкой».
        if (row.author_kind === "persona") {
          return (
            <Space size={10} align="center">
              <Avatar size={32} src={row.persona_avatar_url}>
                {(row.persona_name ?? "?").charAt(0).toUpperCase()}
              </Avatar>
              <Space orientation="vertical" size={0}>
                <Typography.Text style={{ fontWeight: 500 }}>
                  {row.persona_name}
                </Typography.Text>
                <Tag color="purple">{t("comments.persona.tag")}</Tag>
              </Space>
            </Space>
          );
        }
        // Логин оператора здесь не показывается: кто именно отвечал, видно в
        // журнале действий.
        return <Tag color="blue">{t("comments.support")}</Tag>;
      },
    },
    {
      title: t("comments.robot"),
      dataIndex: "robot_name",
      width: 140,
    },
    {
      title: t("comments.text"),
      key: "text",
      render: (_, row) => (
        <Space orientation="vertical" size={2} style={{ width: "100%" }}>
          {row.rating === null || row.rating === undefined ? null : (
            <Rate disabled value={row.rating} style={{ fontSize: 12 }} />
          )}
          <Typography.Paragraph
            style={{ marginBottom: 0 }}
            ellipsis={{ rows: 3, expandable: true, symbol: t("comments.expand") }}
          >
            {row.text}
          </Typography.Paragraph>
          {row.moderation_reason ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t("comments.reason", { reason: row.moderation_reason })}
            </Typography.Text>
          ) : null}
          {row.is_seed ? <Tag color="magenta">{t("comments.testData")}</Tag> : null}
        </Space>
      ),
    },
    {
      title: t("comments.signals"),
      key: "signals",
      width: 130,
      render: (_, row) => (
        <Space size={4} wrap>
          <StatusTag status={row.status} />
          {row.reports_count > 0 ? (
            <Tooltip title={t("comments.reportsHint")}>
              <Tag color="red">{t("comments.reports", { count: row.reports_count })}</Tag>
            </Tooltip>
          ) : null}
          {row.helpful_count > 0 ? (
            <Tag>{t("comments.helpful", { count: row.helpful_count })}</Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: t("common.createdAt"),
      dataIndex: "created_at",
      width: 150,
      render: (value: string) => formatDateTime(value),
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 400,
      render: (_, row) => (
        <CommentActions id={row.id} status={row.status} authorKind={row.author_kind} />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        section="comments"
        title={t("comments.title")}
        subtitle={t("comments.subtitle")}
        extra={
          <Space wrap>
            {isSuperadmin ? (
              <CreatePersonaCommentButton
                // Очередь по умолчанию показывает только то, что ждёт решения,
                // а синтетический отзыв публикуется сразу. Без переключения
                // фильтра он не появился бы в таблице — и это читалось бы как
                // «не сохранилось».
                onCreated={() => {
                  setStatuses(["published"]);
                  setOnlyReported(false);
                }}
              />
            ) : null}
            <Space size={6}>
              <Switch
                checked={onlyReported}
                onChange={setOnlyReported}
                id="only-reported"
              />
              <label htmlFor="only-reported">{t("comments.onlyReported")}</label>
            </Space>
            <StatusFilter value={statuses} onChange={setStatuses} statuses={STATUSES} />
          </Space>
        }
      />

      {query.isError && (
        <Alert
          type="error"
          showIcon
          message={errorMessage(query.error)}
          style={{ marginBottom: 16 }}
        />
      )}

      <SectionCard color={SECTIONS.comments.color} bodyPadding={0}>
        <Table<AdminComment>
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={query.isPending}
          pagination={false}
          scroll={{ x: 1400 }}
          locale={{
            emptyText: (
              <EmptyState
                icon={<MessageOutlined aria-hidden />}
                title={t("comments.empty")}
                hint={t("comments.emptyHint")}
              />
            ),
          }}
        />
        <LoadMore
          hasMore={query.hasNextPage}
          loading={query.isFetchingNextPage}
          onClick={() => {
            void query.fetchNextPage();
          }}
        />
      </SectionCard>
    </>
  );
}
